import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Table, Button, Tooltip } from "@renderbus/ui";
import { useGlobalStore } from "@/store/GlobalStore";
import { Zone } from "project-config/constants/user-info";
import { useInvoiceStore } from "../store/invoice-store";
import { useGetInvoiceOrderTable } from "../hooks/useInitInvoiceOrderTable";
import { useGetSelectData } from "../helper/invoiceSelection";
import { useHandleOpenInvoice } from "../helper/order-operate";
import { useCallback, useMemo, useState } from "react";
import {
  IInvoiceOrderDetail,
  useInitOrderInfo,
} from "../hooks/useInitOrderInfo";
import SearchBar, { SearchWrapper } from "@/components/search-bar";
import { getInvoiceOperateSearchConfig } from "../helper/search-config";
import { PAY_METHOD_PRE_INVOICE } from "../constants";
import { RightOutlined } from "@ant-design/icons";
import { InvoiceCompanyModal } from "./invoice-company-modal";
import { useToastStore } from "@/store/toast-store";
import { PaymentTypeCode } from "project-config/constants";

const InvoiceOperateWrapper = styled.div`
  position: relative;
  padding: 30px 24px 20px;
  .invoice-operate-tip {
    display: flex;
    align-items: center;
    color: var(--main-color);
    cursor: pointer;
    pointer-events: auto;
    svg {
      color: var(--main-color);
    }
  }
  .invoice-disabled-btn {
    background: var(--color-invoice-button-disalbe) !important;
    border-color: var(--color-invoice-button-disalbe) !important;
    color: #ffffff;
    cursor: inherit;
    pointer-events: none;
  }
`;
export const InvoiceSearch = styled.div`
  display: flex;
  margin-bottom: 30px;
  align-items: center;
  button,
  button:hover,
  button:focus {
    background: #333336;
    border-radius: 3px;
    display: flex;
    align-items: center;
    padding: 10px 10px;
    border: 0;
  }
  ${SearchWrapper} {
    flex-wrap: wrap;
    width: 100%;
  }
`;
const InvoiceOperateTip = styled.div`
  float: right;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  position: absolute;
  right: 24px;
  top: 42px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  line-height: 20px;
  pointer-events: none;
  svg {
    margin-left: 6px;
  }
`;
const InvoiceOperateFooter = styled.div`
  height: 72px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  width: 100%;
  padding-top: 16px;
  margin-top: 100px;
  color: rgba(255, 255, 255, 0.85);
  button {
    width: 140px;
    height: 40px;
    margin-left: 48px;
  }
`;
const InvoiceOperateSum = styled.span`
  color: #ff9900;
  padding-right: 4px;
  font-size: 20px;
  font-weight: 500;
  line-height: 18px;
`;
const InvoiceSelectNum = styled.span`
  margin-right: 72px;
`;
const InvoiceTooltips = styled.div`
  position: relative;
  display: flex;
`;

export default function InvoiceOperate() {
  const { t } = useTranslation("invoice");
  const { userInfo } = useGlobalStore();
  const { orderSelectIds, orderSelectSum, setShowInvoiceTip } =
    useInvoiceStore();
  const { invoiceOrderInfo, invoiceOrderForm, fetchInvoiceOrder } =
    useInitOrderInfo();
  const { onSelect, onSelectAll, initTable } = useGetSelectData();
  const { handleOpenInvoice } = useHandleOpenInvoice();
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const columns = useGetInvoiceOrderTable();
  const { showGlobalToast } = useToastStore();
  const selectedBusinessType = useMemo(
    () =>
      invoiceOrderInfo.items
        .filter((i) => orderSelectIds.includes(i.payRecordId))
        .map((i) =>
          i.payMethod === PaymentTypeCode.DesktopAlipay ||
          i.payMethod === PaymentTypeCode.DesktopWechat
            ? 1
            : 0
        ),
    [orderSelectIds, invoiceOrderInfo.items]
  );

  const _handleOpenInvoice = useCallback(() => {
    if (selectedBusinessType.some((i) => i !== selectedBusinessType[0])) {
      showGlobalToast({
        type: "error",
        content: t("invoice_select_different_business_tip"),
      });
      return;
    }
    handleOpenInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOpenInvoice, selectedBusinessType]);
  return (
    <InvoiceOperateWrapper>
      <InvoiceSearch>
        <SearchBar
          searchConfig={getInvoiceOperateSearchConfig()}
          searchForm={invoiceOrderForm}
          showButtonIcon
          onSearch={() => fetchInvoiceOrder(1)}
        />
      </InvoiceSearch>
      <InvoiceOperateTip>
        <div
          className="invoice-operate-tip"
          onClick={() => setShowInvoiceTip(true)}
        >
          {t("invoice_pay_method_invoice_pre")}
          <RightOutlined />
        </div>
        <div>{t("invoice_recharge_sub_tip")}</div>
      </InvoiceOperateTip>
      <Table
        dataSource={invoiceOrderInfo.items}
        columns={columns}
        rowKey={(record: IInvoiceOrderDetail) => record.payRecordId}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: orderSelectIds,
          onSelect: (_, selected, selectedRows) => onSelect(selectedRows),
          onSelectAll: (_, selected) => onSelectAll(selected),
          getCheckboxProps: (record) => {
            // 开具发票预充值每次只能选一个，其他选项disabled，除开具发票的其他可以多选，详见开具发票说明
            const result = orderSelectIds.find((p) =>
              invoiceOrderInfo.items.find(
                (item) =>
                  p === item.payRecordId &&
                  item.payMethod === PAY_METHOD_PRE_INVOICE
              )
            );
            if (!orderSelectIds.length) {
              return {
                disabled: false,
              };
            }
            if (
              result &&
              (record.payMethod !== PAY_METHOD_PRE_INVOICE ||
                (!orderSelectIds.includes(record.payRecordId) &&
                  record.payMethod === PAY_METHOD_PRE_INVOICE))
            ) {
              return {
                disabled: true,
              };
            }
            if (!result && record.payMethod === PAY_METHOD_PRE_INVOICE) {
              return {
                disabled: true,
              };
            }
            return {
              disabled: false,
            };
          },
        }}
        total={invoiceOrderInfo.total}
        showQuickJumper={true}
        defaultPageSize={15}
        scroll={{ x: true, y: 396 }}
        paginationCurrent={currentPageNum}
        onPageNumChange={(key: number) => {
          initTable();
          setCurrentPageNum(key);
          fetchInvoiceOrder(key);
        }}
        className="invoice-table"
      />
      <InvoiceOperateFooter>
        <InvoiceSelectNum>
          {t("invoice_select_num", { count: orderSelectIds.length })}
        </InvoiceSelectNum>
        <InvoiceTooltips>
          {t("add_invoice_invoice_money")}
          <span>{userInfo?.zone === Zone.Domestic ? "" : "$"}</span>
          <Tooltip
            placement="topLeft"
            tooltipText={
              <InvoiceOperateSum>{orderSelectSum}</InvoiceOperateSum>
            }
          />
          <span>
            {userInfo?.zone === Zone.Domestic ? t("invoice_amount_china") : ""}
          </span>
        </InvoiceTooltips>
        <Button
          type="primary"
          className={!orderSelectIds.length ? "invoice-disabled-btn" : ""}
          onClick={_handleOpenInvoice}
        >
          {t("invoice_confirm_btn")}
        </Button>
      </InvoiceOperateFooter>
      <InvoiceCompanyModal />
    </InvoiceOperateWrapper>
  );
}
